import { Pipe, PipeTransform } from '@angular/core';
import { Learning_Type } from '@app/enums/_index';

import { User } from '@app/models/user.model';
import { environment } from '@environments/environment';

@Pipe({
  name: 'courseTypeRoute',
  pure: false,
  standalone: false,
})
export class CourseTypeRoutePipe implements PipeTransform {
  /**
   * Return transformed route to redirect a course to external url or internal routing (default angular routing)
   * based on learning type, user (signed in) or not, and a matching course id that corresponds
   * to User's enrollment course id.
   * NOTE: Pipe must be impure due to the aynchronous nature of the user service which contains the user subscription and enrollments.
   * @param user - If user is signed in, then the transformed type will return a different display type.
   * @param learningType - learning type to transform to new display type
   * @param slugName - If the course slug name is provided.
   * @param courseId - Optional. Course id used to check if the user has this enrollment
   * @param returnUrl - Optional.  If return url is provided, redirect user after login
   * @returns - transformed route to an external url or internal url
   */
  public transform(
    user: User,
    learningType: string,
    slugName: string,
    subscriptionId?: number,
    courseId?: number,
    returnUrl?: string,
    parentSlugName?: string
  ): string {
    switch (learningType) {
      case Learning_Type.SUBSCRIPTION:
        if (slugName) {
          return slugName;
        } else {
          return `pagenotfound`;
        }
      case Learning_Type.E_LEARNING:
      case Learning_Type.QUICK_LEARNING:
      case Learning_Type.WEBINAR:
      case Learning_Type.WORKSHOP:
      case Learning_Type.CERTIFICATION_EXAM:
        if (user) {
          if (user.enrollments && user.enrollments.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            const courseEnrollment = user.enrollments.find(
              (enrollment) => enrollment.id === courseId
            );
            const matchingSubscription =
              subscriptionId &&
              user.activeSubscriptions &&
              user.activeSubscriptions.length > 0 &&
              user.activeSubscriptions.find((subscription) => subscription.id === subscriptionId);
            const isSubscribed = !!matchingSubscription;
            if (courseEnrollment) {
              // User has enrollment based on the course id (which is part of the subscription)
              // NOTE: Check enrollment and redirect user to the external route type which is an external url
              // NOTE: User enrollment url which has the learn/course/{courseId}/{courseSlug}
              // Ex. /learn/course/13062/genesys-cloud-speech-and-text-analytics-sentiment-analysis-elearning
              return `${environment.docebo.url}${courseEnrollment.url}`;
            } else {
              if (isSubscribed) {
                // NOTE: Path has elearning in lower case not mixed case as Learning_Type.E_LEARNING
                return `course/${courseId}`;
              } else {
                // User is not allowed to enroll into this course
                // Pre-prend the course to route to properly redirect to course detail (internal route type)
                return `course/${courseId}`;
              }
            }
          } else {
            if (courseId) {
              return `course/${courseId}`;
            } else {
              return `pagenotfound`;
            }
          }
        } else {
          // User needs to login on sign in
          // NOTE: Route to login page if user is signed in, but must re-route back to target parent slug
          // (ex subscription detail page)
          if (returnUrl) {
            let newReturnUrl = returnUrl;
            if (returnUrl === 'subscription') {
              newReturnUrl = `subscription/${parentSlugName}`;
            } else if (returnUrl === 'search') {
              newReturnUrl = `search`; // NOTE: This is used for unique route button ids
            } else {
              newReturnUrl = `course/${courseId}`; // NOTE: This is used for unique route button ids
            }
            return `${newReturnUrl}`;
          } else {
            if (courseId) {
              return `course/${courseId}`;
            } else {
              return `pagenotfound`;
            }
          }
        }
      case Learning_Type.SELF_STUDY:
      case Learning_Type.INSTRUCTOR_LED:
      case Learning_Type.INSTRUCTOR_LED_IN_PERSON:
      case Learning_Type.INSTRUCTOR_LED_VIRTUAL:
      case Learning_Type.VIRTUAL:
      case Learning_Type.IN_PERSON:
        // Redirect to course/${slugName} regardless if user is signed in or signed out
        // NOTE: This is used for unique route button ids
        if (courseId) {
          return `course/${courseId}`;
        } else {
          return `pagenotfound`;
        }
      default:
        // If slugname is not available, redirect to page not found
        if (courseId) {
          return `course/${courseId}`;
        } else {
          return `pagenotfound`;
        }
    }
  }
}
